/**
 * SDK 套餐列表页红包 banner
 *
 * 不复用主站 CouponClaimEntry（依赖 Next.js Link + @tanstack/react-query）。
 * 红包 banner 图：移动端/PC 各一张，经 <picture>+<source media> 按视口只加载一张；
 * 点击弹框列出券列表（SdkCouponClaimModal，不跳新页），与主流程弹框领取形式一致。
 *
 * 展示逻辑与主站 CouponClaimEntry 保持一致：进页拉一次券包详情（coupon-group-detail）
 * 做存在性校验，仅当券包返回 active 券非空（即 active 且在展示时段）时才渲染入口，
 * 避免指向空活动的无效 CTA。Banner 图取自详情的 banner_mobile_url / banner_pc_url，
 * 任一为空时回退默认红包 Banner 图。
 * code 解析：宿主页 URL ?code= 优先，回退 homepage（镜像主站 couponGroupCode）。
 * 加载中/出错/空券包均不渲染（返回 null）。
 */
import { useEffect, useState } from 'react';
import { fetchGroupDetail, readCouponGroupCode } from '../api/coupons';
import { SdkCouponClaimModal } from './SdkCouponClaimModal';

/** 默认红包 Banner 图（接口 banner_*_url 为空时兜底） */
const DEFAULT_BANNER_MOBILE =
  'https://ai-room-genie.oss-cn-hangzhou.aliyuncs.com/coupon-groups/2026/07/28/73bf6726705d4a52906ed695d57e0531.png';
const DEFAULT_BANNER_PC =
  'https://ai-room-genie.oss-cn-hangzhou.aliyuncs.com/coupon-groups/2026/07/28/9b01f8fbacd448fbbcab50d28f127fc2.png';

type BannerState =
  | { status: 'loading' }
  | { status: 'ready'; mobile: string; pc: string }
  | { status: 'hidden' };

export function CouponBanner() {
  // 与主站 CouponClaimEntry 一致的 code 解析：?code= 优先，回退 homepage
  const couponGroupCode = readCouponGroupCode();
  const code = couponGroupCode || 'homepage';

  const [state, setState] = useState<BannerState>({ status: 'loading' });
  // 领红包弹框开关:点入口弹框列券(不跳新页),与主流程弹框领取一致
  const [claimOpen, setClaimOpen] = useState(false);

  // 进页拉一次券包详情做存在性校验;fetchGroupDetail 模块级缓存,切日期重渲染不重复请求
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const detail = await fetchGroupDetail(code);
      if (cancelled) return;
      // Banner 图：接口字段为空时回退默认红包 Banner 图
      const mobile = detail?.banner_mobile_url || DEFAULT_BANNER_MOBILE;
      const pc = detail?.banner_pc_url || DEFAULT_BANNER_PC;
      // 仅当券包返回 active 券非空时展示
      if (detail && detail.coupons.length > 0) {
        setState({ status: 'ready', mobile, pc });
      } else {
        setState({ status: 'hidden' });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [code]);

  // 加载中/出错/空券包/无图：不渲染（与主站 CouponClaimEntry 一致）
  if (state.status !== 'ready') return null;

  const handleClick = () => {
    setClaimOpen(true);
  };

  return (
    <>
      <button type="button" className="hh-banner" onClick={handleClick}>
        <picture>
          <source media="(max-width: 640px)" srcSet={state.mobile} />
          <img className="hh-banner__img" src={state.pc} alt="领红包" />
        </picture>
      </button>
      {claimOpen && <SdkCouponClaimModal code={code} onClose={() => setClaimOpen(false)} />}
    </>
  );
}
